import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Paper from "@material-ui/core/Paper";
import Grow from "@material-ui/core/Grow";
import Popper from "@material-ui/core/Popper";
import Divider from "@material-ui/core/Divider";

// @material-ui/icons
import Person from "@material-ui/icons/Person";
import ExpandMore from "@material-ui/icons/ExpandMore";
import AccountBalance from "@material-ui/icons/AccountBalance";

// core components
import UnidSel from "components/UnidadeSel";
import Unidade from "./UnidMenuItem";

import {
  primaryColor,
  grayColor,
  whiteColor
} from "assets/jss/material-dashboard-pro-react.js";
import styles from "assets/jss/material-dashboard-pro-react/components/adminNavbarLinksStyle.js";

const useStyles = makeStyles(styles);

const useLocalStyles = makeStyles({
  wrapper: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    border: "none",
    borderRadius: "22px",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background .2s ease"
  },
  unitTrigger: {
    background: grayColor[13],
    padding: "8px 14px",
    color: grayColor[2],
    fontSize: "14px",
    fontWeight: 500,
    maxWidth: "240px",
    "&:hover": { background: grayColor[8] },
    "& > svg:first-child": { fontSize: "20px", color: primaryColor[0] }
  },
  unitName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px"
  },
  profileTrigger: {
    background: "transparent",
    padding: "5px 10px 5px 5px",
    "&:hover": { background: grayColor[13] }
  },
  avatar: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    background:
      "linear-gradient(135deg, " +
      primaryColor[4] +
      " 0%, " +
      primaryColor[1] +
      " 100%)",
    color: whiteColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { fontSize: "22px" }
  },
  profileName: {
    fontSize: "14px",
    fontWeight: 600,
    color: grayColor[2]
  },
  chevron: {
    fontSize: "18px",
    color: grayColor[0]
  },
  popperPanel: {
    zIndex: 1300
  }
});

export default function HeaderLinks(props) {
  const classes = useStyles();
  const local = useLocalStyles();
  const { rtlActive } = props;

  const [openUnidade, setOpenUnidade] = React.useState(null);
  const handleToggleUnidade = (event) => {
    if (openUnidade && openUnidade.contains(event.target)) {
      setOpenUnidade(null);
    } else {
      setOpenUnidade(event.currentTarget);
    }
  };
  const handleCloseUnidade = () => setOpenUnidade(null);

  const [openProfile, setOpenProfile] = React.useState(null);
  const handleClickProfile = (event) => {
    if (openProfile && openProfile.contains(event.target)) {
      setOpenProfile(null);
    } else {
      setOpenProfile(event.currentTarget);
    }
  };
  const handleCloseProfile = () => setOpenProfile(null);

  const dropdownItem = classNames(classes.dropdownItem, classes.primaryHover, {
    [classes.dropdownItemRTL]: rtlActive
  });

  const btnLogout = () => {
    localStorage.removeItem("lukos-token-fw");
    localStorage.removeItem("acesso");
    setTimeout(() => {
      window.location.href = "/auth/login-page";
    }, 0);
  };

  const isLogged = !!localStorage.getItem("lukos-token-fw");
  if (!isLogged) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={local.wrapper}>
      {/* Seletor de unidade operacional */}
      <button
        type="button"
        className={classNames(local.trigger, local.unitTrigger)}
        aria-owns={openUnidade ? "unidade-menu-list-grow" : null}
        aria-haspopup="true"
        onClick={handleToggleUnidade}
      >
        <AccountBalance />
        <span className={local.unitName}>
          <UnidSel />
        </span>
        <ExpandMore className={local.chevron} />
      </button>
      <Popper
        open={Boolean(openUnidade)}
        anchorEl={openUnidade}
        transition
        disablePortal
        placement="bottom-end"
        className={classNames(local.popperPanel, {
          [classes.popperClose]: !openUnidade
        })}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="unidade-menu-list-grow"
            style={{
              transformOrigin:
                placement === "bottom-end" ? "right top" : "right bottom"
            }}
          >
            <div>
              <ClickAwayListener onClickAway={handleCloseUnidade}>
                <div>
                  <Unidade
                    onClose={handleCloseUnidade}
                    onLogout={btnLogout}
                  />
                </div>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>

      {/* Perfil do usuário */}
      <button
        type="button"
        className={classNames(local.trigger, local.profileTrigger)}
        aria-owns={openProfile ? "profile-menu-list" : null}
        aria-haspopup="true"
        onClick={handleClickProfile}
      >
        <span className={local.avatar}>
          <Person />
        </span>
        <span className={local.profileName}>
          {rtlActive ? "الملف الشخصي" : "Usuário"}
        </span>
        <ExpandMore className={local.chevron} />
      </button>
      <Popper
        open={Boolean(openProfile)}
        anchorEl={openProfile}
        transition
        disablePortal
        placement="bottom-end"
        className={classNames(local.popperPanel, {
          [classes.popperClose]: !openProfile
        })}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            id="profile-menu-list"
            style={{ transformOrigin: "right top" }}
          >
            <Paper className={classes.dropdown}>
              <ClickAwayListener onClickAway={handleCloseProfile}>
                <MenuList role="menu">
                  <MenuItem
                    onClick={handleCloseProfile}
                    className={dropdownItem}
                  >
                    {rtlActive ? "الإعدادات" : "Configurações"}
                  </MenuItem>
                  <Divider light />
                  <MenuItem onClick={btnLogout} className={dropdownItem}>
                    {rtlActive ? "الخروج" : "Sair"}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}

HeaderLinks.propTypes = {
  rtlActive: PropTypes.bool
};
